import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { smartlookIdentifyUser, smartlookReapplyIdentity } from 'utils/analytics/smartlook';

interface SmartlookUser {
  id?: number | string | null;
  firstName?: string | null;
  lastName?: string | null;
  cognitoUsername?: string | null;
  email?: string | null;
}

interface SmartlookIdentityProps {
  user?: SmartlookUser;
  tenant?: string;
  project?: string;
}

export function useSmartlookIdentity({ user, tenant, project }: SmartlookIdentityProps) {
  const isInitialized = useRef(false);
  const [lastKnownProject, setLastKnownProject] = useState<string | undefined>(project);

  useEffect(() => {
    if (project) {
      setLastKnownProject(project);
    }
  }, [project]);

  // Preserve last known project so admin tab (project=undefined) doesn't blank it out
  const effectiveProject = project ?? lastKnownProject;

  const identityData = useMemo(() => {
    const name = user
      ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || user.cognitoUsername || undefined
      : undefined;
    return {
      name,
      email: user?.email,
      tenant,
      project: effectiveProject,
    };
  }, [user, tenant, effectiveProject]);

  useEffect(() => {
    if (!user?.id) return;

    // Every call generates a new unique visitor ID — no retroactive property bleed
    smartlookIdentifyUser(user, { tenant, project: effectiveProject });
    isInitialized.current = true;
  }, [tenant, effectiveProject, user]);

  // Re-apply same identity (no new ID) when returning from inactivity
  const reapply = useCallback(() => {
    if (!user?.id) return;
    smartlookReapplyIdentity(user, { tenant, project: effectiveProject });
  }, [user, tenant, effectiveProject]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isInitialized.current) {
        reapply();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [reapply]);

  return identityData;
}
